import { useEffect, useMemo, useState } from "react";
import {
  Bookmark,
  Eye,
  Folder,
  FileText,
  Search,
  ChevronRight,
  Landmark,
  Scale,
  Clock,
  Briefcase,
  CheckCircle2,
  Cloud,
} from "lucide-react";
import { ACTION_BTN_GOLD, ACTION_BTN_ICON } from "../styles/actionButtons";
import { Link, useNavigate } from "react-router-dom";
import { db, storage } from "../firebase";
import { collection, getDocs, query, where, doc, updateDoc } from "firebase/firestore";
import { ref as storageFileRef, getMetadata } from "firebase/storage";
import { auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";

import SelectMenu from "../components/crm/SelectMenu";
// Nominal storage quota shown on the "Storage Used" bar (display only).
const STORAGE_QUOTA_GB = 100;

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// "Case Files" (My Files) — mockup layout: stat cards, search + court/type
// filters, and a folder card per case. Clicking a card opens the same
// per-case Case Files screen as before (/portal/case/:id/case-files).
export default function MyFiles() {
  const [fileData, setFileData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [courtFilter, setCourtFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchData(user);
      } else {
        setError("User not authenticated.");
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchData = async (user) => {
    setLoading(true);
    setError(null);

    try {
      const lawyerId = user.uid;

      // Fetch all files for this lawyer
      const filesSnapshot = await getDocs(
        query(collection(db, "files"), where("lawyer_uid", "==", lawyerId))
      );

      // Get unique case numbers and group files by case
      const caseNumberToDocId = {};
      const caseNumbers = new Set();

      filesSnapshot.docs.forEach((document) => {
        const caseNumber = document.data().caseNo || "";
        if (caseNumber) {
          caseNumbers.add(caseNumber);
        }
      });

      // Fetch document IDs for all case numbers
      for (const caseNumber of caseNumbers) {
        // Try pending first
        const pendingSnapshot = await getDocs(
          query(
            collection(db, "pending"),
            where("caseNo", "==", caseNumber),
            where("owner", "==", lawyerId)
          )
        );

        if (!pendingSnapshot.empty) {
          caseNumberToDocId[caseNumber] = pendingSnapshot.docs[0].id;
        } else {
          // Try disposed
          const disposedSnapshot = await getDocs(
            query(
              collection(db, "disposed"),
              where("caseNo", "==", caseNumber),
              where("owner", "==", lawyerId)
            )
          );

          if (!disposedSnapshot.empty) {
            caseNumberToDocId[caseNumber] = disposedSnapshot.docs[0].id;
          }
        }
      }

      // Group files by document ID
      const groupedFiles = {};
      const docIdToCaseNumber = {};

      filesSnapshot.docs.forEach((document) => {
        const data = document.data();
        const caseNumber = data.caseNo || "";
        const fileName = data.fileName || "";
        const fileUrl = data.file_url || "";

        if (!caseNumber) return;

        const docId = caseNumberToDocId[caseNumber];
        if (!docId) return;

        if (!groupedFiles[docId]) {
          groupedFiles[docId] = [];
          docIdToCaseNumber[docId] = caseNumber;
        }

        groupedFiles[docId].push({
          fileName,
          fileUrl,
          uploadedAt: data.uploadedAt || "",
          fileSize: data.fileSize || 0,
          fileDocId: document.id,
        });
      });

      const data = [];

      // Process each case by document ID
      for (const [docId, files] of Object.entries(groupedFiles)) {
        const caseNumber = docIdToCaseNumber[docId] || "";

        const caseInfo = {
          documentId: docId,
          caseNo: caseNumber,
          files: files,
          fileCount: files.length,
        };

        // Fetch case details using document ID
        try {
          const pendingDocRef = await getDocs(
            query(collection(db, "pending"), where("__name__", "==", docId))
          );

          // NOTE: caseInfo is spread LAST — the case doc has its own `files`
          // field (plain URL strings) that would otherwise clobber our grouped
          // file objects (fileName/uploadedAt/fileSize).
          if (!pendingDocRef.empty) {
            const casdata = pendingDocRef.docs[0].data();
            data.push({
              ...casdata,
              ...caseInfo,
              caseStatus: "pending",
            });
          } else {
            const disposedDocRef = await getDocs(
              query(collection(db, "disposed"), where("__name__", "==", docId))
            );

            if (!disposedDocRef.empty) {
              const casdata = disposedDocRef.docs[0].data();
              data.push({
                ...casdata,
                ...caseInfo,
                caseStatus: "disposed",
              });
            }
          }
        } catch (e) {
          console.error("Error fetching case details:", e);
          continue;
        }
      }

      setFileData(data);
      backfillFileSizes(data);
    } catch (err) {
      console.error("Firestore Fetch Error:", err);
      setError(`Failed to load files. Error: ${err.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  // Exact sizes for older files: fetch Storage metadata for Firebase-hosted
  // files missing `fileSize`, cache the result back on the file doc so this
  // runs at most once per file. Court-hosted files (external URLs) have no
  // readable size and are skipped.
  const backfillFileSizes = async (cases) => {
    const targets = [];
    cases.forEach((c) =>
      (c.files || []).forEach((f) => {
        if (!f.fileSize && f.fileUrl && f.fileUrl.includes("firebasestorage.googleapis.com")) {
          targets.push(f);
        }
      })
    );
    if (!targets.length) return;

    // Small batches so we don't fire hundreds of metadata calls at once.
    const BATCH = 10;
    let updated = false;
    for (let i = 0; i < targets.length; i += BATCH) {
      await Promise.allSettled(
        targets.slice(i, i + BATCH).map(async (f) => {
          try {
            const meta = await getMetadata(storageFileRef(storage, f.fileUrl));
            if (meta?.size) {
              f.fileSize = meta.size;
              updated = true;
              if (f.fileDocId) {
                updateDoc(doc(db, "files", f.fileDocId), { fileSize: meta.size }).catch(() => {});
              }
            }
          } catch {
            /* file gone or not accessible — leave size unknown */
          }
        })
      );
    }
    if (updated) setFileData((prev) => [...prev]);
  };

  // Filter options + stats derived from the loaded cases.
  const courts = useMemo(
    () =>
      [...new Set(fileData.map((c) => c.courtName || c.court || "").filter(Boolean))].sort(),
    [fileData]
  );
  const caseTypes = useMemo(
    () => [...new Set(fileData.map((c) => c.caseType || "").filter(Boolean))].sort(),
    [fileData]
  );
  const stats = useMemo(() => {
    const totalDocs = fileData.reduce((n, c) => n + (c.fileCount || 0), 0);
    const totalBytes = fileData.reduce(
      (n, c) => n + (c.files || []).reduce((m, f) => m + (f.fileSize || 0), 0),
      0
    );
    return { totalCases: fileData.length, totalDocs, totalBytes };
  }, [fileData]);
  const usedPct = Math.min(100, (stats.totalBytes / (STORAGE_QUOTA_GB * 1024 * 1024 * 1024)) * 100);

  const filtered = fileData.filter((caseData) => {
    const court = caseData.courtName || caseData.court || "";
    if (courtFilter !== "all" && court !== courtFilter) return false;
    if (typeFilter !== "all" && (caseData.caseType || "") !== typeFilter) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (caseData.caseNo || "").toLowerCase().includes(q) ||
      (caseData.petitionerName || "").toLowerCase().includes(q) ||
      (caseData.respondentName || "").toLowerCase().includes(q) ||
      court.toLowerCase().includes(q)
    );
  });

  const STAT_CARDS = [
    { label: "Total Cases", value: stats.totalCases, hint: "View all", onClick: () => navigate("/portal/AllCases"), Icon: Briefcase, tint: "bg-indigo-50 text-indigo-600" },
    { label: "Total Documents", value: stats.totalDocs, hint: "View all documents", Icon: FileText, tint: "bg-emerald-50 text-emerald-600" },
    { label: "Total Size", value: formatBytes(stats.totalBytes) || "0 MB", hint: "Across all cases", Icon: Cloud, tint: "bg-sky-50 text-sky-600" },
  ];

  return (
    <div className="crm-compact min-h-screen bg-[#F8FAFC] text-gray-900">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm">
          <Link to="/portal/dashboard" className="text-slate-500 hover:text-slate-800">Home</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="text-slate-500">Documents</span>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Case Files</span>
        </div>

        {/* Header */}
        <div className="mt-2">
          <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Case Files</h1>
          <p className="mt-0.5 text-xs text-slate-500">All case files and documents organized by cases</p>
        </div>

        {/* Stat cards */}
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {STAT_CARDS.map(({ label, value, hint, onClick, Icon, tint }) => (
            <div key={label} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3.5 shadow-sm">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tint}`}>
                <Icon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-[11px] font-medium text-slate-500">{label}</p>
                <p className="text-lg font-bold leading-tight text-slate-900">{value}</p>
                {hint && (
                  onClick ? (
                    <button onClick={onClick} className="text-[11px] font-medium text-blue-600 hover:underline">
                      {hint}
                    </button>
                  ) : (
                    <span className="text-[11px] text-slate-400">{hint}</span>
                  )
                )}
              </div>
            </div>
          ))}

          {/* Storage Used — with usage bar (mockup) */}
          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3.5 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                <Cloud className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-[11px] font-medium text-slate-500">Storage Used</p>
                <p className="text-lg font-bold leading-tight text-slate-900">
                  {formatBytes(stats.totalBytes) || "0 MB"}
                  <span className="ml-1 text-[11px] font-medium text-slate-400">/ {STORAGE_QUOTA_GB} GB</span>
                </p>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-indigo-600"
                  style={{ width: `${Math.max(usedPct, stats.totalBytes > 0 ? 2 : 0)}%` }}
                />
              </div>
              <span className="text-[10px] font-medium text-slate-400">{usedPct.toFixed(1)}%</span>
            </div>
          </div>
        </div>

        {/* Toolbar: search + filters + bookmarks */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-64">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by case title or number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
            />
          </div>
          <SelectMenu
            value={courtFilter}
            onChange={(e) => setCourtFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-yellow-400 focus:outline-none"
          >
            <option value="all">All Courts</option>
            {courts.map((c) => (
              <option key={c} value={c}>{c.length > 40 ? `${c.slice(0, 40)}…` : c}</option>
            ))}
          </SelectMenu>
          <SelectMenu
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-yellow-400 focus:outline-none"
          >
            <option value="all">All Case Types</option>
            {caseTypes.map((t) => (
              <option key={t} value={t}>{t.length > 40 ? `${t.slice(0, 40)}…` : t}</option>
            ))}
          </SelectMenu>
          <div className="ml-auto">
            <button
              onClick={() => navigate("/portal/Bookmarks")}
              className={ACTION_BTN_GOLD}
            >
              <Bookmark className={ACTION_BTN_ICON} />
              Bookmarks
            </button>
          </div>
        </div>

        {/* Content */}
        {loading && (
          <div className="mt-6 flex justify-center py-20">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-yellow-500 border-t-transparent" />
          </div>
        )}

        {error && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-center text-red-600">
            {error}
          </div>
        )}

        {!loading && !error && fileData.length > 0 && filtered.length === 0 && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white py-16 text-center text-slate-500 shadow-sm">
            No cases match your search or filters.
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((caseData) => (
              <CaseFolderCard key={caseData.documentId} caseData={caseData} navigate={navigate} />
            ))}
          </div>
        )}

        {!loading && !error && fileData.length === 0 && (
          <p className="mt-4 text-center text-sm text-slate-500">
            <Folder size={16} className="mr-1 inline-block text-slate-400" />
            No files yet — open a case and upload documents.
          </p>
        )}
      </main>
    </div>
  );
}

// One folder card per case (mockup style). Clicking anywhere opens the same
// per-case Case Files screen as the old "View" icon did.
function CaseFolderCard({ caseData, navigate }) {
  const caseNo = caseData.caseNo || "N/A";
  const petitionerName = caseData.petitionerName || "";
  const respondentName = caseData.respondentName || "";
  const caseType = caseData.caseType || "";
  const courtName = caseData.courtName || caseData.court || "";
  const fileCount = caseData.fileCount || 0;
  const files = caseData.files || [];

  // Latest upload date/time + total size across this case's files.
  const latestUpload = files.reduce((max, f) => {
    const t = f.uploadedAt ? new Date(f.uploadedAt).getTime() : 0;
    return isNaN(t) ? max : Math.max(max, t);
  }, 0);
  const latestDate = latestUpload
    ? new Date(latestUpload).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "";
  const latestTime = latestUpload
    ? new Date(latestUpload).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : "";
  const caseBytes = files.reduce((n, f) => n + (f.fileSize || 0), 0);

  let partyNames = "";
  if (petitionerName && respondentName) {
    partyNames = `${petitionerName} vs ${respondentName}`;
  } else {
    partyNames = petitionerName || respondentName || "";
  }

  return (
    <div
      onClick={() => navigate(`/portal/case/${caseData.documentId}/case-files?from=myfiles`)}
      className="flex cursor-pointer flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow duration-200 hover:shadow-md"
      title={`Open files for ${caseNo}`}
    >
      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-500">
            <Folder size={22} fill="currentColor" />
          </span>
          {/* File-count dot — top-RIGHT of the folder icon (mockup). No
              `font-bold`/`text-white` classes: lightMode.css overrides both
              with dark colors (!important), which made the number invisible. */}
          <span
            className="absolute -right-2 -top-2 flex h-[22px] min-w-[22px] items-center justify-center rounded-full px-1.5 text-[11px] ring-2 ring-white"
            style={{ backgroundColor: "#0f172a", color: "#ffffff", fontWeight: 700 }}
          >
            {fileCount}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="line-clamp-2 text-sm font-bold leading-snug text-slate-900" title={caseNo}>
            {caseNo}
          </h2>
          {partyNames && (
            <p className="mt-0.5 line-clamp-2 text-xs text-slate-600" title={partyNames}>
              {partyNames}
            </p>
          )}
        </div>
        {/* View tile (old My Files style) — right side, opens the case's files */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/portal/case/${caseData.documentId}/case-files?from=myfiles`);
          }}
          title="View files"
          className="group flex h-[92px] w-[84px] shrink-0 cursor-pointer flex-col overflow-hidden rounded-lg border-2 border-slate-200 bg-slate-100 transition-colors hover:border-yellow-500"
        >
          <div className="flex flex-1 items-center justify-center">
            <FileText size={32} className="text-slate-400" />
          </div>
          <div className="flex h-6 items-center justify-center gap-1 bg-yellow-500">
            <Folder size={11} style={{ color: "#ffffff" }} />
            <span className="text-[10px] font-semibold" style={{ color: "#ffffff" }}>
              View Files
            </span>
          </div>
        </div>
      </div>

      <div className="mt-2.5 space-y-1">
        {courtName && (
          <p className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <Landmark size={12} className="shrink-0 text-slate-400" />
            <span className="truncate" title={courtName}>{courtName}</span>
          </p>
        )}
        {caseType && (
          <p className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <Scale size={12} className="shrink-0 text-slate-400" />
            <span className="truncate" title={caseType}>{caseType}</span>
          </p>
        )}
      </div>

      {/* Footer (mockup): size + files | last upload date + time */}
      <div className="mt-3 grid grid-cols-2 divide-x divide-slate-100 border-t border-slate-100 pt-2.5 text-[11px] text-slate-500">
        <div className="flex items-center gap-2 pr-2">
          <FileText size={15} className="shrink-0 text-slate-400" />
          <span className="leading-tight">
            <span className="block text-xs font-semibold text-slate-800">
              {formatBytes(caseBytes) || "—"}
            </span>
            {fileCount} {fileCount === 1 ? "File" : "Files"}
          </span>
        </div>
        <div className="flex items-center gap-2 pl-3">
          <Clock size={15} className="shrink-0 text-slate-400" />
          <span className="leading-tight">
            <span className="block text-xs font-semibold text-slate-800">{latestDate || "—"}</span>
            {latestTime || " "}
          </span>
        </div>
      </div>

      {/* View Case — opens the case's details page (not its files) */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/portal/case/${caseData.documentId}`);
        }}
        className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50"
      >
        <Eye className="h-4 w-4" /> View Case
      </button>
    </div>
  );
}
